<template>
  <!--
    Portal root: moved to <body> on mount so the overlay escapes the
    /tracey page's stacking context (that page is position:fixed;z-index:1,
    which would otherwise cap this overlay below the app header and stop the
    profile button from being dimmed/highlighted).
  -->
  <div class="tt-tut-portal">
    <transition name="tt-tut-fade">
      <div
        v-if="value"
        class="tt-tut"
        role="dialog"
        aria-modal="true"
        aria-label="Tracey walkthrough"
      >
      <!--
        Spotlight: a transparent box positioned over the current target whose
        enormous box-shadow paints the dim everywhere EXCEPT the target, so the
        highlighted control stays fully visible and fully clickable (the layer
        itself is pointer-events:none). No part of the screen is covered.
      -->
      <div v-if="hole" class="tt-tut__spotlight" :style="spotlightStyle" />
      <div v-else class="tt-tut__scrim" />

      <!-- Step card with an arrow pointing at the highlighted control -->
      <div
        ref="tooltip"
        class="tt-tut__card"
        :class="`tt-tut__card--${arrowSide}`"
        :style="cardStyle"
      >
        <span
          v-if="hole"
          class="tt-tut__arrow"
          :class="`tt-tut__arrow--${arrowSide}`"
          :style="arrowStyle"
        />

        <div class="tt-tut__head">
          <span class="tt-tut__step-count"
            >Step {{ index + 1 }} of {{ steps.length }}</span
          >
          <button
            type="button"
            class="tt-tut__close"
            aria-label="Skip walkthrough"
            @click="finish(true)"
          >
            <v-icon size="18" color="#8e8ea0">mdi-close</v-icon>
          </button>
        </div>

        <h3 class="tt-tut__title">{{ current.title }}</h3>
        <div class="tt-tut__body">
          <p v-for="(line, i) in current.body" :key="i" class="tt-tut__line">
            {{ line }}
          </p>
        </div>
        <p v-if="current.hint" class="tt-tut__hint">
          <v-icon size="15" color="#ff1463">mdi-gesture-tap</v-icon>
          {{ current.hint }}
        </p>

        <div class="tt-tut__dots" aria-hidden="true">
          <span
            v-for="(s, i) in steps"
            :key="i"
            class="tt-tut__dot"
            :class="{ 'tt-tut__dot--active': i === index }"
          />
        </div>

        <div class="tt-tut__actions">
          <button type="button" class="tt-tut__skip" @click="finish(true)">
            Skip
          </button>
          <div class="tt-tut__nav">
            <button
              v-if="index > 0"
              type="button"
              class="tt-tut__btn tt-tut__btn--ghost"
              @click="prev"
            >
              Back
            </button>
            <button
              type="button"
              class="tt-tut__btn tt-tut__btn--primary"
              @click="next"
            >
              {{ isLast ? "Got it" : "Next" }}
              <v-icon
                v-if="!isLast"
                size="16"
                color="#fff"
                class="tt-tut__btn-icon"
                >mdi-arrow-right</v-icon
              >
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "TraceyTutorial",
  model: { prop: "value", event: "input" },
  props: {
    value: { type: Boolean, default: false },
    steps: { type: Array, required: true }
  },
  data() {
    return {
      index: 0,
      hole: null, // { top, left, width, height, radius }
      cardPos: { top: 0, left: 0 },
      arrowSide: "top", // side of the CARD the arrow sits on, pointing at target
      arrowOffset: 24, // px along the arrow's edge
      // Bound listeners kept so they can be removed cleanly.
      onReflow: null
    };
  },
  computed: {
    current() {
      return this.steps[this.index] || {};
    },
    isLast() {
      return this.index >= this.steps.length - 1;
    },
    spotlightStyle() {
      if (!this.hole) return {};
      return {
        top: `${this.hole.top}px`,
        left: `${this.hole.left}px`,
        width: `${this.hole.width}px`,
        height: `${this.hole.height}px`,
        borderRadius: `${this.hole.radius}px`
      };
    },
    cardStyle() {
      return {
        top: `${this.cardPos.top}px`,
        left: `${this.cardPos.left}px`
      };
    },
    arrowStyle() {
      // Along-edge position of the arrow so it points at the target centre.
      if (this.arrowSide === "top" || this.arrowSide === "bottom") {
        return { left: `${this.arrowOffset}px` };
      }
      return { top: `${this.arrowOffset}px` };
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.index = 0;
        this.$nextTick(() => this.activateStep());
      } else {
        this.teardownListeners();
        document.body.classList.remove("tracey-tutorial-active");
      }
    }
  },
  mounted() {
    // Move the portal root to <body> so the overlay is not confined to the
    // /tracey page's stacking context (see the template comment).
    if (this.$el && document.body && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
    if (this.value) {
      this.$nextTick(() => this.activateStep());
    }
  },
  beforeDestroy() {
    this.teardownListeners();
    document.body.classList.remove("tracey-tutorial-active");
    // Remove the portal root we relocated to <body>.
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    activateStep() {
      document.body.classList.add("tracey-tutorial-active");
      this.$emit("step", { index: this.index, key: this.current.key });
      // Give the page a tick to react to step-change (e.g. open the sidebar),
      // then measure. A second pass covers late layout shifts.
      this.$nextTick(() => {
        this.updatePositions();
        setTimeout(this.updatePositions, 60);
        setTimeout(this.updatePositions, 220);
      });
      this.setupListeners();
    },
    setupListeners() {
      if (this.onReflow) return;
      this.onReflow = () => this.updatePositions();
      window.addEventListener("resize", this.onReflow, { passive: true });
      window.addEventListener("scroll", this.onReflow, {
        passive: true,
        capture: true
      });
    },
    teardownListeners() {
      if (!this.onReflow) return;
      window.removeEventListener("resize", this.onReflow);
      window.removeEventListener("scroll", this.onReflow, { capture: true });
      this.onReflow = null;
    },
    updatePositions() {
      const step = this.current;
      const el = step.selector ? document.querySelector(step.selector) : null;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 12;
      const tip = this.$refs.tooltip;
      const tw = tip ? tip.offsetWidth : 340;
      const th = tip ? tip.offsetHeight : 180;

      if (!el) {
        // No target on screen: dim everything and centre the card.
        this.hole = null;
        this.cardPos = {
          top: Math.max(margin, (vh - th) / 2),
          left: Math.max(margin, (vw - tw) / 2)
        };
        return;
      }

      const r = el.getBoundingClientRect();
      const pad = step.padding == null ? 8 : step.padding;
      const hole = {
        top: Math.max(0, r.top - pad),
        left: Math.max(0, r.left - pad),
        width: Math.min(vw, r.width + pad * 2),
        height: Math.min(vh, r.height + pad * 2),
        radius: step.radius == null ? 14 : step.radius
      };
      this.hole = hole;

      const gap = 16;
      const holeCX = hole.left + hole.width / 2;
      const holeCY = hole.top + hole.height / 2;
      let placement = step.placement || "auto";

      // Resolve 'auto' and fall back when a side has no room.
      const roomTop = hole.top;
      const roomBottom = vh - (hole.top + hole.height);
      const roomRight = vw - (hole.left + hole.width);
      const roomLeft = hole.left;
      if (placement === "auto") {
        if (roomBottom >= th + gap + margin) placement = "bottom";
        else if (roomTop >= th + gap + margin) placement = "top";
        else if (roomRight >= tw + gap + margin) placement = "right";
        else placement = "left";
      } else if (placement === "top" && roomTop < th + gap + margin) {
        placement = "bottom";
      } else if (placement === "bottom" && roomBottom < th + gap + margin) {
        placement = "top";
      } else if (placement === "right" && roomRight < tw + gap + margin) {
        placement = "left";
      } else if (placement === "left" && roomLeft < tw + gap + margin) {
        placement = "right";
      }

      let top;
      let left;
      // The arrow sits on the card edge FACING the target.
      if (placement === "top") {
        top = hole.top - gap - th;
        left = holeCX - tw / 2;
        this.arrowSide = "bottom";
      } else if (placement === "bottom") {
        top = hole.top + hole.height + gap;
        left = holeCX - tw / 2;
        this.arrowSide = "top";
      } else if (placement === "right") {
        left = hole.left + hole.width + gap;
        top = holeCY - th / 2;
        this.arrowSide = "left";
      } else {
        left = hole.left - gap - tw;
        top = holeCY - th / 2;
        this.arrowSide = "right";
      }

      // Clamp within the viewport.
      left = Math.min(Math.max(margin, left), vw - tw - margin);
      top = Math.min(Math.max(margin, top), vh - th - margin);
      this.cardPos = { top, left };

      // Point the arrow at the target centre along the card edge.
      if (this.arrowSide === "top" || this.arrowSide === "bottom") {
        this.arrowOffset = Math.min(Math.max(18, holeCX - left), tw - 30);
      } else {
        this.arrowOffset = Math.min(Math.max(18, holeCY - top), th - 30);
      }
    },
    next() {
      if (this.isLast) {
        this.finish(false);
        return;
      }
      this.index += 1;
      this.activateStep();
    },
    prev() {
      if (this.index === 0) return;
      this.index -= 1;
      this.activateStep();
    },
    finish(skipped) {
      this.$emit("finish", { skipped });
      this.$emit("input", false);
    }
  }
};
</script>

<style scoped>
/* Portal wrapper lives at <body> level and takes no space of its own. */
.tt-tut-portal {
  position: static;
}

.tt-tut {
  position: fixed;
  inset: 0;
  z-index: 2000;
  pointer-events: none; /* let the highlighted control stay clickable */
  /* Match the Tracey chat screen's font: the overlay is portalled to <body>,
     so it would otherwise inherit the app's default (Roboto) instead. */
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
}

/* Full dim used when a step has no on-screen target. */
.tt-tut__scrim {
  position: absolute;
  inset: 0;
  background: rgba(13, 13, 13, 0.55);
}

.tt-tut__spotlight {
  position: absolute;
  background: transparent;
  box-shadow: 0 0 0 100vmax rgba(13, 13, 13, 0.55);
  border: 2px solid #ff1463;
  transition: top 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    left 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  animation: tt-tut-pulse 2s ease-in-out infinite;
}

@keyframes tt-tut-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 100vmax rgba(13, 13, 13, 0.55),
      0 0 0 0 rgba(255, 20, 99, 0.45);
  }
  50% {
    box-shadow: 0 0 0 100vmax rgba(13, 13, 13, 0.55),
      0 0 0 7px rgba(255, 20, 99, 0.16);
  }
}

.tt-tut__card {
  position: absolute;
  width: 340px;
  max-width: calc(100vw - 24px);
  pointer-events: auto;
  background: #fff;
  border-radius: 16px;
  padding: 18px 18px 14px;
  box-shadow: 0 12px 40px rgba(12, 37, 58, 0.28);
  transition: top 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    left 0.32s cubic-bezier(0.4, 0, 0.2, 1);
}

.tt-tut__arrow {
  position: absolute;
  width: 16px;
  height: 16px;
  background: #fff;
  transform: rotate(45deg);
  box-shadow: 0 12px 40px rgba(12, 37, 58, 0.28);
}
.tt-tut__arrow--top {
  top: -7px;
}
.tt-tut__arrow--bottom {
  bottom: -7px;
}
.tt-tut__arrow--left {
  left: -7px;
}
.tt-tut__arrow--right {
  right: -7px;
}

.tt-tut__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.tt-tut__step-count {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #ff1463;
}

.tt-tut__close {
  display: inline-flex;
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
}
.tt-tut__close:hover {
  background: #f2f4f7;
}

.tt-tut__title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #12253a;
}

.tt-tut__body {
  margin: 0;
}

.tt-tut__line {
  margin: 0 0 6px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #475467;
}
.tt-tut__line:last-child {
  margin-bottom: 0;
}

.tt-tut__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 0 0;
  padding: 7px 10px;
  border-radius: 8px;
  background: #fff0f5;
  color: #c01059;
  font-size: 12.5px;
  font-weight: 600;
}

.tt-tut__dots {
  display: flex;
  gap: 6px;
  margin: 14px 0 12px;
}

.tt-tut__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e4e7ec;
  transition: background 0.2s, width 0.2s;
}
.tt-tut__dot--active {
  width: 20px;
  border-radius: 4px;
  background: #ff1463;
}

.tt-tut__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tt-tut__skip {
  border: none;
  background: transparent;
  color: #8e8ea0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 4px;
}
.tt-tut__skip:hover {
  color: #667085;
}

.tt-tut__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tt-tut__btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  border-radius: 9px;
  padding: 8px 16px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}

.tt-tut__btn--ghost {
  background: #f2f4f7;
  color: #344054;
}
.tt-tut__btn--ghost:hover {
  background: #e4e7ec;
}

.tt-tut__btn--primary {
  background: #ff1463;
  color: #fff;
}
.tt-tut__btn--primary:hover {
  background: #e60f58;
}

.tt-tut__btn-icon {
  margin-right: -2px;
}

/* Enter / leave */
.tt-tut-fade-enter-active,
.tt-tut-fade-leave-active {
  transition: opacity 0.25s ease;
}
.tt-tut-fade-enter,
.tt-tut-fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Not scoped: while the walkthrough runs, lift the agent-select dropdown above
   the spotlight dim so the user can actually see and pick an agent in step 1.
   !important is required to beat Vuetify's inline z-index on .v-menu__content. */
body.tracey-tutorial-active .v-menu__content.tracey-agent-select__menu {
  z-index: 2200 !important;
}
</style>
